import { Box } from '@mui/material';
import { useApi } from '@hooks/useApi';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid } from '@features/dashboard/widgets/grid';
import { ColDef, RowDataUpdatedEvent } from 'ag-grid-enterprise';
import { useUserProductsAndTenorsContext } from '@shared/contexts/UserProductsAndTenorsProvider';
import { FreemiumTickersDataResponse, Ticker } from '../types/types';

export const FreemiumPrices = () => {
  const { apiClient } = useApi();
  const [freemiumTickers, setFreemiumTickers] = useState<FreemiumTickersDataResponse>({});
  const [products, setProducts] = useState<string[]>([]);
  const [numberOfTenors, setNumberOfTenors] = useState<number>(0);
  const [adminDelay, setAdminDelay] = useState<number>(15);
  const [timestamp, setTimestamp] = useState<string>('');

  const productColumns = useMemo(() => {
    return products.map(symbol => ({
      field: symbol,
      headerName: symbol,
    }));
  }, [products]);

  const tenors = useUserProductsAndTenorsContext()
    .tenors.filter(t => t.frequency !== 'daily' && t.frequency !== 'weekly')
    .slice(0, numberOfTenors);

  const fetchFreemiumData = useCallback(async () => {
    if (!apiClient) return;

    const tickers: FreemiumTickersDataResponse = await apiClient?.getFreemiumTickers();
    const categoryResponse = await apiClient?.getFreemiumData({ category: 'tickers' });
    const responseNumOfTenors = categoryResponse?.[0]?.data?.tenors;
    const responseProducts = categoryResponse?.[0]?.data?.product_symbols;
    const responseDelay = categoryResponse?.[0]?.data?.delay ?? 15;
    const responseTimestamp = tickers?.[responseProducts?.[0] || '']?.[0]?.timestamp;

    setNumberOfTenors(responseNumOfTenors ?? 0);
    setProducts(responseProducts || []);
    setFreemiumTickers(tickers || {});
    setAdminDelay(responseDelay);
    setTimestamp(responseTimestamp);
  }, [apiClient]);

  const calculateDelay = useCallback(() => {
    if (!timestamp) return 0;
    const now = new Date().getTime();
    const nextUpdateTime = adminDelay * 2 * 60 * 1000 + new Date(timestamp).getTime();
    const diff = nextUpdateTime - now;
    return diff / (60 * 1000);
  }, [timestamp, adminDelay]);

  useEffect(() => {
    fetchFreemiumData();
  }, [fetchFreemiumData]);

  useEffect(() => {
    if (!apiClient || !timestamp) return;

    let timeout: NodeJS.Timeout;
    const scheduleNextUpdate = () => {
      const delay = calculateDelay();
      if (delay <= 0) {
        fetchFreemiumData();
        return;
      }
      timeout = setTimeout(() => {
        fetchFreemiumData();
        scheduleNextUpdate();
      }, delay * 60 * 1000);
    };

    scheduleNextUpdate();

    return () => clearTimeout(timeout);
  }, [apiClient, fetchFreemiumData, calculateDelay]);

  const rowData = useMemo(() => {
    return tenors.map(t => ({
      tenor: t.display.toLocaleUpperCase(),
      ...products.reduce((acc, symbol) => {
        acc[symbol] = freemiumTickers[symbol]?.find((ticker: Ticker) => ticker?.symbol?.includes(t.code))?.close;
        return acc;
      }, {}),
    }));
  }, [tenors, products, freemiumTickers]);

  const colDefs: ColDef[] = [
    {
      field: 'tenor',
      headerName: '',
      sortable: false,
    },
    ...productColumns,
  ];
  return (
    <Box sx={{ width: '100%', height: '100%', p: 0.1, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Grid
          columnDefs={colDefs}
          rowData={rowData}
          onRowDataUpdated={(params: RowDataUpdatedEvent) => {
            params.api.sizeColumnsToFit();
          }}
          autoSizeStrategy={{ type: 'fitGridWidth' }}
          defaultColDef={{
            valueFormatter: params => {
              if (params.column.getColId() === 'tenor') return params.value;
              return params.value ? Number(params.value).toFixed(2) : '0.00';
            },
          }}
        />
      </Box>
    </Box>
  );
};
